/** The computer-use approval gate's own question ("Allow Denker to …?"). It
 *  asks at the agent cursor, at the target point when the gate names one,
 *  even while a conversation frame shows the run. */
export const DESKTOP_APPROVAL = "desktop_approval";

/** A global logical point (renderers subtract their window origin) and its display. */
export interface DesktopApprovalPoint {
  displayId: number;
  globalX: number;
  globalY: number;
}

export interface DesktopApprovalFields {
  kind?: typeof DESKTOP_APPROVAL;
  desktopTarget?: DesktopApprovalPoint;
}

/** The approval marker and target point from a `cursor.question` payload or
 *  a pending awaiting-input row; empty for any other question. */
export function desktopApprovalFields(data: {
  kind?: unknown;
  desktop_target?: unknown;
}): DesktopApprovalFields {
  if (data.kind !== DESKTOP_APPROVAL) return {};
  const target = data.desktop_target as Record<string, unknown> | null | undefined;
  const { x, y, display_id: displayId } = target ?? {};
  return typeof x === "number" &&
    Number.isFinite(x) &&
    typeof y === "number" &&
    Number.isFinite(y) &&
    typeof displayId === "number" &&
    Number.isInteger(displayId)
    ? { kind: DESKTOP_APPROVAL, desktopTarget: { displayId, globalX: x, globalY: y } }
    : { kind: DESKTOP_APPROVAL };
}

/** A desktop approval asked again (rehydrated after a reconnect) keeps the
 *  point it was first asked at, which may have come from the cursor itself. */
export function keepAskedPoint<
  T extends DesktopApprovalFields & { awaitingInputId: string },
>(question: T, previous: T | undefined): T {
  return question.kind &&
    !question.desktopTarget &&
    previous?.awaitingInputId === question.awaitingInputId &&
    previous.desktopTarget
    ? { ...question, desktopTarget: previous.desktopTarget }
    : question;
}
